import styled, { css } from 'styled-components';

import { Box } from '@/components/Box';
import { Flex } from '@/components/Flex';
import { Typography } from '@/components/Typography';

/**
 * BEC-24926: the purchase CTAs sit 8px apart on mobile to keep the primary
 * button above the fold. Desktop keeps the original 16px, which is why the gap
 * is overridden here rather than by changing the gridGap prop.
 */
export const CtaStack = styled(Flex)`
  ${({ theme }) => theme.media.md.down} {
    grid-gap: ${({ theme }) => theme.spacing(1)};
  }

  /* BEC-24926: keep the 14px LegacyButton drops below 600px, to match Call to buy. */
  button {
    font-size: ${({ theme }) => theme.fontSizes.p2};
  }
`;

/**
 * BEC-24926: the purchase CTAs are reordered on mobile only - Call to buy takes
 * the grey button, 4Gift drops to the secondary links - while desktop keeps its
 * current arrangement. The two layouts use different element types in different
 * containers, which CSS cannot reorder, so both are rendered and one is hidden
 * per breakpoint.
 *
 * `display: none` is deliberate: it removes the hidden copy from the
 * accessibility tree and the tab order, so a screen reader is never offered
 * both. Do not swap it for opacity or off-screen positioning, which leave the
 * element exposed.
 */
export const DesktopOnly = styled(Box)`
  ${({ theme }) => theme.media.md.down} {
    display: none;
  }
`;

export const MobileOnly = styled(Box)`
  ${({ theme }) => theme.media.md.up} {
    display: none;
  }
`;

/**
 * BEC-24926: the 3D viewer, change-strap and marketing-selector buttons are
 * desktop only, but their inline-flex row still rendered on mobile as an empty
 * element. Being inline-level it generated a line box worth ~14px of blank
 * space between the financing text and Add to bag, so it is hidden outright.
 */
export const DesktopIconRow = styled(Box)`
  display: inline-flex;
  flex-direction: row;

  ${({ theme }) => theme.media.md.down} {
    display: none;
  }
`;

export const ButtonWrapper = styled(Box)`
  position: absolute;
  max-height: 52px;
  z-index: 2;
  padding: 8px 8px 16px 8px;
  border-radius: 6px;
  ${({ theme }) => css`
    right: ${theme.spacing(1)};
    bottom: 0;
  `}
`;

export const Text = styled(Typography)`
  width: fit-content;
  line-height: 1.25;

  ${({ theme }) => css`
    margin-top: ${theme.spacing(1)};
    font-size: ${theme.fontSizes.p2};
    color: ${theme.colors.primaryDark};
    font-weight: bold;
    border-bottom: 1px solid ${theme.colors.primary};
  `}
`;
